import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card, Table, Button, Tag, Space, Typography, Upload, Modal, Select, Progress,
  Popconfirm, Tooltip, Empty, Descriptions,
} from "antd";
import {
  CloudUploadOutlined, InboxOutlined, ReloadOutlined, RocketOutlined,
  ExperimentOutlined, PauseCircleOutlined, PlayCircleOutlined,
} from "@ant-design/icons";
import {
  listReleases, getReleaseRollout, setReleaseCanary, publishRelease,
  pauseReleaseRollout, resumeReleaseRollout, uploadAgentRelease, listCameras,
} from "../network/api";
import { notify, serverError } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import { relativeTime } from "../constants/health";

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

const CHANNEL_META = {
  draft: { color: "default", label: "Draft" },
  canary: { color: "gold", label: "Canary" },
  published: { color: "green", label: "Published" },
  superseded: { color: "default", label: "Superseded" },
};

const kb = (n) => (n ? `${Math.round(n / 1024)} KB` : "—");
const shortSha = (s) => (s ? `${s.slice(0, 12)}…` : "—");

// Fleet release rollout — upload a tarball, canary it on test devices, then
// "Make public" to converge the whole fleet. Devices pull the target on their
// next poll; offline/returning/new devices converge automatically.
export default function Releases() {
  const { isHardwareAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollouts, setRollouts] = useState({}); // filename -> { stats, rollout }
  const [uploading, setUploading] = useState(false);
  const [canaryFor, setCanaryFor] = useState(null); // release row being canaried
  const [canaryIds, setCanaryIds] = useState([]);
  const [devices, setDevices] = useState([]); // fleet roster for the canary picker
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [busy, setBusy] = useState(null); // filename currently mutating

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listReleases());
    } catch (e) {
      notify.error(serverError(e, "Failed to load releases"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Pull live rollout stats for the active targets (canary/published).
  const loadRollouts = useCallback(async (list) => {
    const targets = list.filter((r) => r.channel === "canary" || r.channel === "published");
    const entries = await Promise.all(
      targets.map(async (r) => {
        try {
          return [r.filename, await getReleaseRollout(r.filename)];
        } catch {
          return [r.filename, null];
        }
      })
    );
    setRollouts(Object.fromEntries(entries.filter(([, v]) => v)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!rows.length) return;
    loadRollouts(rows);
    const t = setInterval(() => loadRollouts(rows), 15000); // live progress
    return () => clearInterval(t);
  }, [rows, loadRollouts]);

  const onUpload = async (file) => {
    setUploading(true);
    try {
      const { filename } = await uploadAgentRelease(file);
      notify.success(`Uploaded ${filename} — now a Draft. Canary it, then Make public.`);
      await load();
    } catch (e) {
      notify.error(serverError(e, "Release upload failed"));
    } finally {
      setUploading(false);
    }
    return false; // block antd default upload
  };

  const act = async (filename, fn, okMsg) => {
    setBusy(filename);
    try {
      await fn();
      notify.success(okMsg);
      await load();
    } catch (e) {
      notify.error(serverError(e, "Action failed"));
    } finally {
      setBusy(null);
    }
  };

  // Roster for the canary picker. Loaded lazily on first open and kept —
  // the fleet is small enough to hold in one page.
  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const res = await listCameras({ page: 1, pageSize: 1000, sortField: "deviceId", sortOrder: "asc" });
      setDevices(res?.data || []);
    } catch (e) {
      notify.error(serverError(e, "Failed to load devices"));
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  // Known devices, plus any already-canaried ID that no longer resolves so it
  // still renders as a tag instead of silently vanishing.
  const deviceOptions = useMemo(() => {
    const opts = devices
      .filter((d) => d.deviceId)
      .map((d) => ({
        value: d.deviceId,
        label: d.deviceTitle ? `${d.deviceId} — ${d.deviceTitle}` : d.deviceId,
      }));
    const known = new Set(opts.map((o) => o.value));
    canaryIds.forEach((id) => {
      if (!known.has(id)) opts.push({ value: id, label: id });
    });
    return opts;
  }, [devices, canaryIds]);

  const openCanary = (row) => {
    setCanaryFor(row);
    setCanaryIds(row.canaryDeviceIds || []);
    if (!devices.length && !devicesLoading) loadDevices();
  };
  const submitCanary = () =>
    act(
      canaryFor.filename,
      () => setReleaseCanary(canaryFor.filename, canaryIds),
      `Canary set on ${canaryIds.length} device(s)`
    ).then(() => setCanaryFor(null));

  const columns = useMemo(
    () => [
      {
        title: "Release",
        dataIndex: "version",
        render: (v, r) => (
          <Space direction="vertical" size={0}>
            <Text strong>{v || r.filename}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.filename}</Text>
          </Space>
        ),
      },
      {
        title: "Channel",
        dataIndex: "channel",
        render: (c, r) => {
          const m = CHANNEL_META[c] || CHANNEL_META.draft;
          const paused = r.rollout?.state === "paused";
          return (
            <Space>
              <Tag color={m.color}>{m.label}</Tag>
              {(c === "canary" || c === "published") && paused && (
                <Tooltip title={r.rollout?.pausedReason || "paused"}>
                  <Tag color="volcano">Paused</Tag>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
      {
        title: "Rollout",
        key: "progress",
        render: (_, r) => {
          const ro = rollouts[r.filename];
          if (!ro || (r.channel !== "canary" && r.channel !== "published")) return <Text type="secondary">—</Text>;
          const { onTarget, total, pending, failed } = ro.stats;
          const pct = total ? Math.round((onTarget / total) * 100) : 0;
          return (
            <Space direction="vertical" size={2} style={{ minWidth: 180 }}>
              <Progress percent={pct} size="small" status={failed ? "exception" : pct === 100 ? "success" : "active"} />
              <Space size={4} wrap>
                <Tag color="green">{onTarget} on target</Tag>
                {pending ? <Tag>{pending} pending</Tag> : null}
                {failed ? <Tag color="red">{failed} failed</Tag> : null}
                <Text type="secondary" style={{ fontSize: 12 }}>/ {total}</Text>
              </Space>
            </Space>
          );
        },
      },
      { title: "sha256", dataIndex: "sha256", render: (s) => <Text code style={{ fontSize: 12 }}>{shortSha(s)}</Text> },
      { title: "Size", dataIndex: "size", render: kb },
      { title: "Uploaded", dataIndex: "uploadedAt", render: (v) => (v ? relativeTime(v) : "—") },
      ...(isHardwareAdmin
        ? [
            {
              title: "Actions",
              key: "actions",
              render: (_, r) => {
                const paused = r.rollout?.state === "paused";
                const isTarget = r.channel === "canary" || r.channel === "published";
                return (
                  <Space wrap>
                    <Button size="small" icon={<ExperimentOutlined />} loading={busy === r.filename} onClick={() => openCanary(r)}>
                      Canary
                    </Button>
                    <Popconfirm
                      title="Make this release public?"
                      description="Every device in the fleet will converge onto it on its next check-in."
                      okText="Make public"
                      onConfirm={() => act(r.filename, () => publishRelease(r.filename), `${r.version || r.filename} is now the fleet target`)}
                      disabled={r.channel === "published" && !paused}
                    >
                      <Button size="small" type="primary" icon={<RocketOutlined />} disabled={r.channel === "published" && !paused} loading={busy === r.filename}>
                        Make public
                      </Button>
                    </Popconfirm>
                    {isTarget &&
                      (paused ? (
                        <Button size="small" icon={<PlayCircleOutlined />} loading={busy === r.filename}
                          onClick={() => act(r.filename, () => resumeReleaseRollout(r.filename), "Rollout resumed")}>
                          Resume
                        </Button>
                      ) : (
                        <Button size="small" danger icon={<PauseCircleOutlined />} loading={busy === r.filename}
                          onClick={() => act(r.filename, () => pauseReleaseRollout(r.filename), "Rollout paused")}>
                          Pause
                        </Button>
                      ))}
                  </Space>
                );
              },
            },
          ]
        : []),
    ],
    [rollouts, isHardwareAdmin, busy] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const expandedRow = (r) => {
    const ro = rollouts[r.filename];
    if (!ro) return <Text type="secondary">No rollout data (not a live target).</Text>;
    const byState = { "on-target": [], pending: [], failed: [] };
    (ro.stats.byDevice || []).forEach((d) => (byState[d.state] || byState.pending).push(d));
    return (
      <Descriptions size="small" column={3} bordered>
        <Descriptions.Item label="On target">{byState["on-target"].map((d) => d.deviceId).join(", ") || "—"}</Descriptions.Item>
        <Descriptions.Item label="Pending">{byState.pending.map((d) => d.deviceId).join(", ") || "—"}</Descriptions.Item>
        <Descriptions.Item label="Failed">
          {byState.failed.map((d) => `${d.deviceId} (${d.failedAttempts}×)`).join(", ") || "—"}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  return (
    <Card
      title="Agent Releases & Fleet Rollout"
      extra={
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Upload an agent release, verify it on a <b>canary</b> device set, then <b>Make public</b> to roll it to the whole fleet.
        Devices converge on their next check-in — including ones that are offline now or imaged later. A rollout auto-pauses if too many devices fail.
      </Paragraph>

      {isHardwareAdmin && (
        <Dragger beforeUpload={onUpload} showUploadList={false} accept=".tgz,.tar.gz" disabled={uploading} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">{uploading ? "Uploading…" : "Drop an edge360 release .tgz here to upload"}</p>
          <p className="ant-upload-hint">The server computes the sha256 the devices verify. Uploaded as a Draft.</p>
        </Dragger>
      )}

      <Table
        rowKey="filename"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        expandable={{ expandedRowRender: expandedRow }}
        locale={{ emptyText: <Empty description="No releases uploaded yet" /> }}
      />

      <Modal
        open={!!canaryFor}
        title={`Canary — ${canaryFor?.version || canaryFor?.filename || ""}`}
        okText={<><ExperimentOutlined /> Set canary</>}
        onOk={submitCanary}
        confirmLoading={busy === canaryFor?.filename}
        onCancel={() => setCanaryFor(null)}
      >
        <Paragraph type="secondary">
          Only these devices will pull this release. Once verified, use <b>Make public</b> to promote it to the fleet.
        </Paragraph>
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder="Pick devices, or type IDs e.g. 27166574"
          value={canaryIds}
          onChange={setCanaryIds}
          tokenSeparators={[",", " "]}
          options={deviceOptions}
          loading={devicesLoading}
          optionFilterProp="label"
          maxTagCount="responsive"
          allowClear
          notFoundContent={devicesLoading ? "Loading devices…" : <Empty description="No devices found" />}
        />
      </Modal>
    </Card>
  );
}
